import { startCase } from 'lodash';

import Workflow from 'models/Workflow';

// The `coverages` value extraction produces (OLIO-2586): one entry per insurance the packet
// listed. It's the only produced value that isn't a scalar, so it's read through here rather than
// inline — both the extraction card and the referral header render it, and they must not drift.

export const COVERAGES_VALUE = 'coverages';

// Keys are camelCase. The value NAME (`coverages`) is snake_case and read verbatim, but the value
// CONTENT is deep-camelCased by olio-api's OliveBranch middleware on the way out — so what's
// stored as `payer_name` arrives here as `payerName`. Reading the stored spelling silently yields
// undefined and the coverage renders blank, which is exactly what happened first time round.
export interface ExtractedCoverage {
  payerName?: string;
  memberId?: string;
  // 'primary' | 'secondary' — kept open because a third is a schema addition on the API side, not
  // a change here. Single word, so camelCasing leaves it alone.
  sequence?: string;
}

// Narrowed defensively: the model types values as scalars while this one holds an array of
// objects, so anything malformed reads as no coverages rather than throwing mid-render.
export function readCoverages(workflow: Workflow): ExtractedCoverage[] {
  const raw = workflow.valueFor(COVERAGES_VALUE)?.value as unknown;
  if (!Array.isArray(raw)) return [];

  return raw.filter((entry): entry is ExtractedCoverage => !!entry && typeof entry === 'object');
}

// "Primary" / "Secondary" — how a coverage is named on screen. Falls back to nothing when the
// entry never carried a sequence, so a label is never invented for it.
export function coverageLabel(coverage: ExtractedCoverage): string {
  return coverage.sequence ? startCase(coverage.sequence) : '';
}

// The coverages the entry form offers, in order. Extraction asks the packet for these same two by
// name, so a typed set and an extracted set describe the same thing.
export const COVERAGE_SEQUENCES = ['primary', 'secondary'] as const;

// One row of the insurance step, before it becomes a coverage. Always strings — an untouched
// input is '' rather than undefined, which is what lets the blank rows drop out below.
export interface CoverageEntry {
  payerName: string;
  memberId: string;
}

export const emptyCoverageEntries = (): CoverageEntry[] =>
  COVERAGE_SEQUENCES.map(() => ({ payerName: '', memberId: '' }));

// The typed rows as the stored `coverages` value: blank rows dropped, blank fields omitted rather
// than sent as '', and each row stamped with the sequence its position means. Omitting is what
// keeps "typed a secondary payer but not its member id" from storing an empty member id that
// reads as a real one downstream.
//
// Sent camelCase on purpose — olio-api's OliveBranch snake_cases request bodies, so `payerName`
// is stored as `payer_name`, the same spelling extraction writes.
// Whether the typed rows describe the same insurance twice, in the way the engine can't tell apart.
// A coverage is identified by member id and payer name TOGETHER, so an identical pair is ONE
// coverage: minting keeps the first and drops the second. Only both fields matching counts —
// sharing just a payer, or just a member id, is two real coverages (one subscriber can hold two
// plans on one id), and referral intake declares an acceptance criteria for exactly that.
//
// Deliberately not exhaustive. A row holding only a payer name will also absorb a later row that
// adds a member id to the same payer, because the fuller identity recognises the partial one. That
// merge is wanted when extraction learns a member id it didn't have at first; catching it here
// would warn about the case the identity rules exist to handle.
export function duplicatesOneInsurance(entries: CoverageEntry[]): boolean {
  const coverages = coveragesFromEntries(entries);
  if (coverages.length < 2) return false;

  const identity = (coverage: ExtractedCoverage) =>
    [coverage.payerName ?? '', coverage.memberId ?? ''].join('|').toLowerCase();
  return new Set(coverages.map(identity)).size < coverages.length;
}

export function coveragesFromEntries(entries: CoverageEntry[]): ExtractedCoverage[] {
  return COVERAGE_SEQUENCES.flatMap((sequence, index) => {
    const payerName = entries[index]?.payerName.trim() ?? '';
    const memberId = entries[index]?.memberId.trim() ?? '';
    if (!payerName && !memberId) return [];

    return [{ ...(payerName ? { payerName } : {}), ...(memberId ? { memberId } : {}), sequence }];
  });
}
